
import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import CancelButton from "../../Components/CommonComponent/Button/CancelButton";
import SaveButton from "../../Components/CommonComponent/Button/SaveButton";
import { useContext } from "react";
import { CommonContext } from "../../App";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { FaChevronDown } from "react-icons/fa";

export default function AddInventory({ editItem, handleSave, handleCancel, loading }) {
  const { branchId } = useContext(CommonContext);
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [form, setForm] = useState({
    ledger_id: "",
    out_side: "",
    in_side: "",
    description: ""
  });

  const [errors, setErrors] = useState({});

  /* -------------------- Prefill on Edit -------------------- */
  useEffect(() => {
    if (editItem) {
      setForm({
        ledger_id: editItem.ledger_id,
        in_side: editItem.in_side,
        out_side: editItem.out_side,
        description: editItem.description || ""
      });
    }
  }, [editItem]);

  /* -------------------- Fetch Items -------------------- */
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await api.get(`ledgers/get-all-ledger?branchId=${branchId}`);
        const filtered = res.data.allData.filter(
          (item) =>
            item.groupCategory === "Bricks" ||
            item.groupCategory === "Raw Material"
        );
        setItems(filtered);
      } catch (err) {
        toast.error("Failed to load items");
      }
    };
    if (branchId) fetchItems();
  }, [branchId]);

  /* -------------------- Outside Click Close -------------------- */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedItem = items.find((i) => i.id === Number(form.ledger_id));

  const handleSelect = (item) => {
    setForm((prev) => ({ ...prev, ledger_id: item.id }));
    setErrors((prev) => ({ ...prev, ledger_id: "" }));
    setOpen(false);
  };

  /* -------------------- Validation -------------------- */
  const validate = () => {
    const newErrors = {};

    if (!form.ledger_id) {
      newErrors.ledger_id = t("selectItemRequired") || "Please select an item";
    }

    const inVal = Number(form.in_side || 0);
    const outVal = Number(form.out_side || 0);

    if (!inVal && !outVal) {
      newErrors.stock = t("stockRequired") || "Enter add or deduct stock";
    }

    if (inVal && outVal) {
      newErrors.stock = t("onlyOneStock") || "Fill only one field (add or deduct)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = () => {
    if (!validate()) return;

    const payload = {
      ledger_id: form.ledger_id,
      in_side: Number(form.in_side || 0),
      out_side: Number(form.out_side || 0),
      description: form.description || "new stock"
    };

    if (editItem) handleSave(payload, editItem.id);
    else handleSave(payload);
  };

  return (
    <div className="bg-white p-2 rounded-md shadow-lg w-[500px]">

      <div className="text-center py-1 bg-theme-headerBg text-white px-2 rounded-1">
        {t("addInventory")}
      </div>

      <div className="mt-3 space-y-3 px-1">


        <div ref={dropdownRef} className="relative">
          <label className="text-sm font-medium">{t("selectItem")}</label>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className={`w-full mt-1 flex justify-between items-center border rounded px-2 py-1 bg-white transition
              ${errors.ledger_id ? "border-red-500" : "border-theme-headerBg hover:border-theme-btnBg"}
            `}
          >
            <span className={selectedItem ? "text-gray-700" : "text-gray-400"}>
              {selectedItem
                ? `${selectedItem.name}${selectedItem.cousinName ? ` (${selectedItem.cousinName})` : ""}`
                : t("selectItem")}
            </span>

            <FaChevronDown
              className={`transition-transform duration-200 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>

          {errors.ledger_id && (
            <p className="text-xs text-red-500 mt-1">{errors.ledger_id}</p>
          )}

          {open && (
            <ul className="absolute left-0 right-0 mt-1 max-h-52 overflow-auto border border-theme-headerBg rounded shadow-lg bg-white z-[9999] p-0  custom-scrollbar">
              {items.map((item) => (
                <li
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className={`px-2 py-1 cursor-pointer hover:bg-theme-tableHoverColor hover:text-theme-headerBg transition ${
                    form.ledger_id === item.id
                      ? "bg-theme-headerBg text-white"
                      : ""
                  }`}
                >
                  {item.name}
                  {item.cousinName && ` (${item.cousinName})`}
                </li>
              ))}
            </ul>
          )}
        </div>

 
        <div>
          <label className="text-sm font-medium">{t("addStock")}</label>
          <input
            type="number"
            name="in_side"
            className={`border p-1 rounded w-full outline-none focus:border-theme-btnBg ${
              errors.stock ? "border-red-500" : "border-theme-headerBg"
            }`}
            value={form.in_side}
            onChange={(e) => {
              setForm({ ...form, in_side: e.target.value });
              setErrors((p) => ({ ...p, stock: "" }));
            }}
            placeholder="Stock (In)"
          />
        </div>

      
        <div>
          <label className="text-sm font-medium">{t("outStock")}</label>
          <input
            type="number"
            name="out_side"
            className={`border p-1 rounded w-full outline-none focus:border-theme-btnBg ${
              errors.stock ? "border-red-500" : "border-theme-headerBg"
            }`}
            value={form.out_side}
            onChange={(e) => {
              setForm({ ...form, out_side: e.target.value });
              setErrors((p) => ({ ...p, stock: "" }));
            }}
            placeholder="Stock (Out)"
          />
        </div>

        {errors.stock && (
          <p className="text-xs text-red-500 -mt-2">{errors.stock}</p>
        )}

        <div>
          <label className="text-sm font-medium">{t("description")}</label>
          <input
            type="text"
            name="description"
            className="border border-theme-headerBg p-1 rounded w-full outline-none focus:border-theme-btnBg"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            placeholder="Description"
          />
        </div>
      </div>

     
      <div className="flex justify-end gap-3 mt-6 mb-2 px-1">
        <CancelButton onClick={handleCancel} text={t("cancel")} />
        <SaveButton onClick={onSubmit} text={t(loading ? "saving" : "save")} disabled={loading} />
      </div>
    </div>
  );
}

